import { GenericLazyString } from '@eventbrite/i18n';
import { ComponentProps, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { DeliveryMethod } from '../../api/models/deliveryMethod';
import { OrderAttendee } from '../../models/attendee';
import {
    getCurrencyFormat,
    getIsAppRebrandActive,
    getIsRegEvent,
} from '../../selectors/app';
import {
    getSelectedDeliveryMethodsForDisplay,
    getTicketGroups,
} from '../../selectors/deliveryMethod';
import {
    getEventImageSrc,
    getEventLogoFocalPoint,
    getEventTitle,
    getFormattedEventDate,
    getShippingRates,
    getWaitlistTicketId,
} from '../../selectors/event';
import { isSeriesInfiniteCalendar } from '../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import {
    getAttendeesSortedByTicketCategory,
    getIsTransferredOrder,
    getMinimumQuantityErrorMessage,
    hasMultipleRoyalties,
    haveSelectedTicketsShippingFee,
    isEventWithShippingFee,
} from '../../selectors/order';
import { getOrderCosts } from '../../selectors/order/getOrderCosts';
import { getUnformattedOrderCost } from '../../selectors/order/getUnformattedOrderCost';
import {
    getDisplayInstallments,
    isAddAttendeesOrder,
} from '../../selectors/order/typedOrderSelectors';
import { getIsPaymentView } from '../../selectors/routing';
import { getTicketsById } from '../../selectors/tickets';
import { getBreakdownItems } from '../../selectors/tickets/breakdownItems';
import { getDisplayCostProps } from '../../selectors/tickets/getDisplayCostProps';
import { getTicketsProps } from '../../selectors/tickets/getTicketsProps';
import { getSelectedVirtualIncentives } from '../../selectors/virtualIncentive';
import { CheckoutState } from '../../state';
import { Pane } from './Pane';

type PaneProps = ComponentProps<typeof Pane>;

export type EventLogoFocalPoint = { x: number; y: number };

const mapStateToProps = (
    state: CheckoutState,
): Pick<
    PaneProps,
    | 'breakdownItems'
    | 'currency'
    | 'currencyFormat'
    | 'eventImageSrc'
    | 'eventTitle'
    | 'installments'
    | 'isAddAttendeesOrder'
    | 'isEventWithShippingFee'
    | 'isPaymentView'
    | 'isTransferredOrder'
    | 'orderCosts'
    | 'selectedTickets'
    | 'selectedVirtualIncentives'
    | 'ticketsById'
    | 'totalCost'
    | 'unformattedOrderCost'
    | 'waitlistTicketId'
> & {
    hasFeeBreakdown: boolean;
    isAppRebrandActive: boolean;
    isRegEvent: boolean;
    quantityErrorMessage: GenericLazyString | null;
    selectedDeliveryMethods: Array<DeliveryMethod>;
    shippingRates: Array<
        | {}
        | {
              backupDeliveryMethod: DeliveryMethod;
              cutoffDate: string;
          }
    >;
    sortedAttendeesByTicketCategory: Array<OrderAttendee>;
    ticketGroups: Array<Array<DeliveryMethod>>;
    totalCostDisplay: string;
    totalCostUnformatted: number;
    totalTicketQuantity: number;
    eventDate: string;
    eventLogoFocalPoint?: EventLogoFocalPoint;
    isSeriesInfiniteCalendar: boolean;
} => {
    const currencyFormat = getCurrencyFormat(state);
    const isRegEvent = getIsRegEvent(state);
    const eventImageSrc = getEventImageSrc(state);
    const eventLogoFocalPoint = getEventLogoFocalPoint(state);
    const eventTitle = getEventTitle(state);
    // Determines if the fees item is an expansion panel or not
    const hasFeeBreakdown =
        hasMultipleRoyalties(state) ||
        getIsTransferredOrder(state) ||
        haveSelectedTicketsShippingFee(state);
    // NB: Should the feeComponents array be stripped out of the Order.costs object or not?
    // TODO: Refactor `includeFeeComponents` into a selector and test it after better understanding
    // where else its used. Stripping-out data in some scenarios and not others seems silly, so
    // hoping to eventually not need this
    const includeFeeComponents = hasFeeBreakdown;
    const installments = getDisplayInstallments(state);
    const orderCosts = getOrderCosts(state, includeFeeComponents);
    const ticketsById = getTicketsById(state);
    const waitlistTicketId = getWaitlistTicketId(state) ?? '';
    const { currency, totalCost, totalCostDisplay, totalCostUnformatted } =
        getDisplayCostProps(state);
    const { selectedTickets, totalTicketQuantity } = getTicketsProps(state);

    return {
        currencyFormat,
        eventImageSrc,
        eventTitle,
        eventLogoFocalPoint,
        hasFeeBreakdown,
        installments,
        isAddAttendeesOrder: isAddAttendeesOrder(state),
        isAppRebrandActive: getIsAppRebrandActive(state),
        isEventWithShippingFee: isEventWithShippingFee(state),
        isPaymentView: getIsPaymentView(state),
        isTransferredOrder: getIsTransferredOrder(state),
        isRegEvent,
        orderCosts,
        quantityErrorMessage: getMinimumQuantityErrorMessage(state),
        selectedDeliveryMethods: getSelectedDeliveryMethodsForDisplay(state),
        eventDate: getFormattedEventDate(state),
        selectedVirtualIncentives: getSelectedVirtualIncentives(state),
        shippingRates: getShippingRates(state),
        sortedAttendeesByTicketCategory:
            getAttendeesSortedByTicketCategory(state),
        ticketGroups: getTicketGroups(state),
        ticketsById,
        waitlistTicketId,
        unformattedOrderCost: getUnformattedOrderCost(state),
        breakdownItems: getBreakdownItems(state),
        currency,
        totalCost,
        totalCostDisplay,
        totalCostUnformatted,
        selectedTickets,
        totalTicketQuantity,
        isSeriesInfiniteCalendar: isSeriesInfiniteCalendar(state),
    };
};

type ConnectedProps = ReturnType<typeof mapStateToProps>;

export const connectPane = <OriginalProps = {}>(
    ComponentClass: FunctionComponent<
        Omit<OriginalProps, keyof ConnectedProps>
    >,
) => connect(mapStateToProps)(ComponentClass);
